import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import Customers from '../shared/Customers';
import Inquiries from '../shared/Inquiries';
import Schedule from '../shared/Schedule';
import { 
  Home, 
  LogOut, 
  MapPin, 
  DollarSign,
  Eye,
  Users,
  TrendingUp,
  Clock,
  MessageSquare,
  CalendarDays,
  AlertTriangle,
  X,
  Phone
} from 'lucide-react';
import { fixImageUrl } from '../../lib/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [staleListings, setStaleListings] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('listings');
  const [listingStats, setListingStats] = useState({
    total: 0,
    available: 0,
    pending: 0,
    sold: 0
  });
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    active: 0
  });
  const [inquiryStats, setInquiryStats] = useState({
    total: 0,
    new: 0,
    pending: 0
  });

  useEffect(() => {
    if (!user || user.role !== 'employee') {
      navigate('/login');
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [listingsRes, customersRes, inquiriesRes, staleRes] = await Promise.all([
        axios.get(`${API}/listings/my-listings`),
        axios.get(`${API}/customers/my-customers`),
        axios.get(`${API}/inquiries/`),
        axios.get(`${API}/listings/stale-alerts`)
      ]);
      
      const listingsData = listingsRes.data;
      const customersData = customersRes.data;
      const inquiriesData = inquiriesRes.data;
      const staleData = staleRes.data;
      
      // Filter inquiries assigned to this employee
      const myInquiries = inquiriesData.filter(inq => inq.assigned_to === user?.id);
      
      setListings(listingsData);
      setCustomers(customersData);
      setInquiries(myInquiries);
      setStaleListings(staleData);
      
      setListingStats({
        total: listingsData.length,
        available: listingsData.filter(l => l.status === 'available').length,
        pending: listingsData.filter(l => l.status === 'pending').length,
        sold: listingsData.filter(l => l.status === 'sold').length
      });
      
      setCustomerStats({
        total: customersData.length,
        active: customersData.filter(c => c.status === 'active').length
      });
      
      setInquiryStats({
        total: myInquiries.length,
        new: myInquiries.filter(i => i.status === 'new').length,
        pending: myInquiries.filter(i => i.status === 'pending' || i.status === 'contacted').length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = (listingId) => {
    setDismissedAlerts([...dismissedAlerts, listingId]);
  };

  const activeAlerts = staleListings.filter(l => !dismissedAlerts.includes(l.id));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Navigation */}
      <nav className="bg-brand-brown text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_property-mgmt-43/artifacts/8gx78s2p_Signe%20logo.jpg" alt="Signé" className="h-8 w-8 rounded-full object-cover" />
            <span className="font-heading text-lg font-bold tracking-tight">Signé <span className="text-brand-gold">Properties</span></span>
          </Link>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand-gold flex items-center justify-center font-heading font-bold text-sm text-brand-brown">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block">
                <p className="font-heading text-sm font-semibold">{user?.name}</p>
                <p className="font-body text-xs text-white/60">Employee</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white/60 hover:text-white hover:bg-white/10 rounded-none"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
              My Dashboard
            </h1>
            <p className="font-body text-muted-foreground mt-1">
              Manage your listings and customers
            </p>
          </div>
          <Link to="/">
            <Button variant="outline" className="rounded-none border-2 font-heading text-xs uppercase tracking-wide" data-testid="view-site-btn">
              View Site
            </Button>
          </Link>
        </div>

        {/* Stale Listing Alerts */}
        {activeAlerts.length > 0 && (
          <div className="space-y-3" data-testid="stale-listing-alerts">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              <h2 className="font-heading text-sm font-bold uppercase tracking-wide">
                Attention Required ({activeAlerts.length})
              </h2>
            </div>
            <div className="grid gap-3">
              {activeAlerts.map((listing) => (
                <Card key={listing.id} className="rounded-none border-amber-300 bg-amber-50/50" data-testid={`stale-alert-${listing.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="rounded-none bg-amber-500 text-white text-[10px] uppercase tracking-widest">
                            {listing.days_active} days
                          </Badge>
                          <span className="font-heading font-semibold text-sm">{listing.title}</span>
                        </div>
                        <p className="font-body text-xs text-muted-foreground flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {listing.district || listing.city || 'No location'} 
                          <span className="mx-1">•</span>
                          <DollarSign className="w-3 h-3" />
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(listing.price)}
                        </p>
                        <p className="font-body text-xs text-amber-700 mt-2">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          No inquiries received. Consider updating photos, adjusting price, or promoting this listing.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link to={`/property/${listing.id}`}>
                          <Button variant="outline" size="sm" className="rounded-none border-amber-300 text-amber-700 hover:bg-amber-100">
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => dismissAlert(listing.id)}
                          className="rounded-none text-muted-foreground hover:text-foreground"
                          data-testid={`dismiss-alert-${listing.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="rounded-none border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Home className="w-4 h-4" />
                My Listings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold" data-testid="stat-listings">
                {listingStats.total}
              </p>
            </CardContent>
          </Card>
          <Card className="rounded-none border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Sold
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-brand-gold" data-testid="stat-sold">
                {listingStats.sold}
              </p>
            </CardContent>
          </Card>
          <Card className="rounded-none border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Users className="w-4 h-4" />
                My Customers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold" data-testid="stat-customers">
                {customerStats.total}
              </p>
            </CardContent>
          </Card>
          <Card className="rounded-none border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                My Inquiries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold" data-testid="stat-inquiries">
                {inquiryStats.total}
              </p>
            </CardContent>
          </Card>
          <Card className="rounded-none border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending Action
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-yellow-600" data-testid="stat-pending-inquiries">
                {inquiryStats.new + inquiryStats.pending}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Listings and Customers */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-secondary rounded-none p-1">
            <TabsTrigger value="listings" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-listings">
              <Home className="w-4 h-4 mr-2" />
              My Listings
            </TabsTrigger>
            <TabsTrigger value="schedule" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-schedule">
              <CalendarDays className="w-4 h-4 mr-2" />
              My Schedule
            </TabsTrigger>
            <TabsTrigger value="customers" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-customers">
              <Users className="w-4 h-4 mr-2" />
              My Customers
            </TabsTrigger>
            <TabsTrigger value="inquiries" className="rounded-none font-heading uppercase tracking-wide text-xs" data-testid="tab-inquiries">
              <MessageSquare className="w-4 h-4 mr-2" />
              My Inquiries {inquiryStats.total > 0 && <Badge className="ml-2 rounded-none bg-brand-gold text-brand-brown text-[10px]">{inquiryStats.total}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            <Card className="rounded-none border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-semibold">Your Assigned Listings</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Property</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Owner</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Price</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Listed</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Inquiries</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest">Status</TableHead>
                      <TableHead className="font-heading text-xs uppercase tracking-widest text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <div className="w-8 h-8 border-4 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
                        </TableCell>
                      </TableRow>
                    ) : listings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <Home className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                          <p className="font-body text-muted-foreground">No listings assigned to you yet</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      listings.map((listing) => (
                        <TableRow key={listing.id} className="hover:bg-secondary/30" data-testid={`listing-row-${listing.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {listing.images && listing.images.length > 0 ? (
                                <img src={fixImageUrl(listing.images[0])} alt={listing.title} className="w-12 h-12 object-cover" />
                              ) : (
                                <div className="w-12 h-12 bg-muted flex items-center justify-center">
                                  <Home className="w-6 h-6 text-muted-foreground" />
                                </div>
                              )}
                              <div>
                                <span className="font-heading font-semibold text-sm block">{listing.title}</span>
                                <span className="font-body text-xs text-muted-foreground flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {listing.city || listing.district || listing.address}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <p className="font-body text-sm font-medium">{listing.owner_name || '—'}</p>
                              {listing.owner_phone && (
                                <a href={`tel:${listing.owner_phone}`} className="font-body text-xs text-brand-gold hover:underline flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {listing.owner_phone}
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-heading font-bold text-brand-gold">
                            {formatPrice(listing.price)}
                          </TableCell>
                          <TableCell>
                            <p className="font-body text-sm">{new Date(listing.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${
                              listing.inquiry_count > 0 
                                ? 'bg-green-500 text-white' 
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {listing.inquiry_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${
                              listing.status === 'sold' 
                                ? 'bg-brand-gold text-brand-brown' 
                                : listing.status === 'pending'
                                ? 'bg-yellow-500 text-white'
                                : 'bg-brand-brown text-white'
                            }`}>
                              {listing.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Link to={`/property/${listing.id}`}>
                              <Button variant="ghost" size="sm" className="rounded-none" data-testid={`view-listing-${listing.id}`}>
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule">
            <Schedule isAdmin={false} />
          </TabsContent>

          <TabsContent value="customers">
            <Customers isAdmin={false} />
          </TabsContent>

          <TabsContent value="inquiries">
            <Inquiries isAdmin={false} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
